import { Component, useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';

import { useIsMobile } from '@/hooks/useIsMobile';
import { historyIdx } from '@/hooks/useNavigateUp';
import { CLOSE_THRESHOLD, useSwipeRight } from '@/hooks/useSwipeRight';
import { useActiveAccount } from '@/store/active-account/active-account.store';
import { getBackHandler } from '@/store/back-handler/back-handler.store';
import { ImpactStyle, triggerHaptic } from '@/utils/haptics';

import s from './EdgeSwipeBack.module.scss';

const SETTLE_MS = 220;
// A back handler that does not change the route (a wizard step, an inner view) never
// triggers the location reset below, so the page slides back in after this long anyway.
const RESET_FALLBACK_MS = 400;
// How long a page returned to is held where it was left while its content settles.
const RESTORE_MS = 2000;
// The page underneath starts this far to the left and dimmed, and catches up as the top one
// slides away, the way iOS shows the screen a back swipe returns to. A page reached by going
// back without a swipe enters from the same place, faded.
const UNDER_OFFSET = 0.3;
const UNDER_DIM = 0.5;
const ENTER_OPACITY = 0.6;
// The moving page's left edge, so it reads as a sheet lying over the one below.
const EDGE_SHADOW = 'var(--side-panel-shadow)';
// ponytail: copies kept for the last 10 steps forward; a deeper swipe back slides over the
// plain background, as it did before. Raise it if that shows up in real use.
const KEEP = 10;

// By place in history: where each page was scrolled when the user left it, and on mobile a
// copy of it as it was when the user went forward from it, so a swipe back can show the page
// it returns to before that page is rendered again. A copy, not the page: it does not
// update, and canvases (charts) come out blank.
const scrollTops = new Map<number, number>();
const copies = new Map<number, HTMLElement>();

// What lies under the page while it is swiped: the copy, when there is one, and a shade.
let layer: { root: HTMLElement; dim: HTMLElement; copy: boolean } | null = null;
// Fixed bars and buttons inside the page, held in place while the page is moved.
let pinned: HTMLElement[] = [];
// Set by the browser when its own back gesture has already animated the page in.
let browserAnimated = false;
// Ends the scroll restore below, if it is still waiting.
let stopRestoring = () => {};

const getPage = () => document.getElementById('main-content');
const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// A transformed element becomes the frame its `position: fixed` descendants (an add button, a
// totals bar) are placed in, and a page, being the scroller, would carry them off by its
// scroll. Each is shifted back to where it was, so it rides along with the frame instead.
const pinFixed = (frame: HTMLElement) => {
  const fixed = [...frame.querySelectorAll<HTMLElement>('*')].filter(
    (el) => el.offsetParent === null && getComputedStyle(el).position === 'fixed',
  );
  // A copy shown before still carries its last shift.
  fixed.forEach((el) => (el.style.translate = ''));
  const before = fixed.map((el) => el.getBoundingClientRect());
  frame.style.transform = 'translateX(0)';
  fixed.forEach((el, i) => {
    const { left, top } = el.getBoundingClientRect();
    el.style.translate = `${before[i].left - left}px ${before[i].top - top}px`;
  });
  return fixed;
};

// Puts a page returned to back where it was left, and holds it there for RESTORE_MS: the page
// may still be loading, too short to reach that position, or refetch and change height above
// it. The first touch, wheel or key hands the scroll back to the user.
const restoreScroll = (page: HTMLElement, top: number) => {
  page.scrollTop = top;
  if (!top) return;
  const until = performance.now() + RESTORE_MS;
  let frame = 0;
  const stop = () => {
    window.cancelAnimationFrame(frame);
    ['pointerdown', 'wheel', 'keydown'].forEach((type) => document.removeEventListener(type, stop));
    stopRestoring = () => {};
  };
  const hold = () => {
    if (Math.abs(page.scrollTop - top) > 1) page.scrollTop = top;
    if (performance.now() > until) stop();
    else frame = window.requestAnimationFrame(hold);
  };
  frame = window.requestAnimationFrame(hold);
  ['pointerdown', 'wheel', 'keydown'].forEach((type) =>
    document.addEventListener(type, stop, { passive: true }),
  );
  stopRestoring = stop;
};

// `progress` runs from 0, the top page in place, to 1, the top page gone.
const moveUnder = (progress: number) => {
  if (!layer) return;
  const offset = reducedMotion() ? 0 : UNDER_OFFSET;
  layer.root.style.transform = `translateX(${-offset * (1 - progress) * 100}%)`;
  layer.dim.style.opacity = String(UNDER_DIM * (1 - progress));
};

// Lays the shade, and the copy of the previous page at the scroll it was left at, under this one.
const showUnder = (page: HTMLElement) => {
  const copy = copies.get(historyIdx() - 1);
  const { top, left, width, height } = page.getBoundingClientRect();
  const root = document.createElement('div');
  root.className = s.under;
  root.inert = true;
  root.setAttribute('aria-hidden', 'true');
  Object.assign(root.style, {
    top: `${top}px`,
    left: `${left}px`,
    width: `${width}px`,
    height: `${height}px`,
  });
  const dim = document.createElement('div');
  dim.className = s.dim;
  if (copy) root.append(copy);
  root.append(dim);
  // Before the page in the tree, so the page, once it moves, paints over it.
  page.before(root);
  if (copy) copy.scrollTop = scrollTops.get(historyIdx() - 1) ?? 0;
  pinFixed(root);
  layer = { root, dim, copy: Boolean(copy) };
  moveUnder(0);
};

const resetPage = () => {
  layer?.root.remove();
  layer = null;
  pinned.forEach((el) => (el.style.translate = ''));
  pinned = [];
  const page = getPage();
  if (!page) return;
  page.getAnimations().forEach((animation) => animation.cancel());
  page.style.transform = '';
  page.style.transition = '';
  page.style.boxShadow = '';
  page.style.background = '';
};

// The page slides in from the left, the way it arrives on a back.
const slideIn = (page: HTMLElement) => {
  if (reducedMotion()) return;
  pinned = pinFixed(page);
  page.animate(
    [
      { transform: `translateX(${-UNDER_OFFSET * 100}%)`, opacity: ENTER_OPACITY },
      { transform: 'translateX(0)', opacity: 1 },
    ],
    {
      duration: SETTLE_MS,
      easing: getComputedStyle(page).getPropertyValue('--ease-standard').trim(),
    },
  ).onfinish = resetPage;
};

// A copy is only looked at: what would act on its own once back in the document goes — ids
// and radio groups the live page also has, and media that would load and play again.
const takeCopy = (page: HTMLElement) => {
  const node = page.cloneNode(true) as HTMLElement;
  node.removeAttribute('id');
  node.removeAttribute('style');
  node.classList.add(s.copy);
  node.querySelectorAll('[id]:not(svg *), input[type="radio"]').forEach((el) => {
    el.removeAttribute('id');
    el.removeAttribute('name');
  });
  node.querySelectorAll('iframe, video, audio, source').forEach((el) => {
    el.removeAttribute('src');
    el.removeAttribute('srcdoc');
    el.removeAttribute('autoplay');
  });
  return node;
};

// getSnapshotBeforeUpdate runs after a navigation has rendered and before the DOM changes,
// the last moment the page being left is still there to read. Function components have no
// equivalent. Only a step forward is copied: a step back or a replace leaves a page that a
// swipe will not return to.
class RememberPageOnLeave extends Component<{ idx: number; copy: boolean }> {
  getSnapshotBeforeUpdate({ idx }: { idx: number }) {
    const page = getPage();
    if (!page || idx === this.props.idx) return null;
    scrollTops.set(idx, page.scrollTop);
    if (this.props.idx > idx) {
      if (this.props.copy) copies.set(idx, takeCopy(page));
      else copies.delete(idx);
      for (const key of copies.keys()) if (Math.abs(key - idx) >= KEEP) copies.delete(key);
    }
    return null;
  }

  // React wants this beside getSnapshotBeforeUpdate.
  componentDidUpdate() {}

  render() {
    return null;
  }
}

/**
 * Swipe right anywhere on the page to go back, the way native apps do. The page follows the
 * finger over a copy of the page it returns to and, when released far or fast enough, runs
 * the active back handler (`useBackHandler`) or, on a nested page without one, goes back in
 * history. Tab roots do not move.
 *
 * Also owns the page's scroll across navigations: a new page opens at the top, and going back
 * or forward by any route lands where that page was left. On mobile, a page reached by going
 * back without a swipe slides in from the left.
 */
export const EdgeSwipeBack = () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const { key, pathname } = useLocation();
  const serviceId = useActiveAccount((state) => state.serviceId);
  const last = useRef<{ key: string; pathname: string; idx: number } | null>(null);
  const settling = useRef(false);
  const pastThreshold = useRef(false);
  const dragFrom = useRef('');
  const timer = useRef(0);

  // The route has rendered: put the page back in place before it paints, and scroll it.
  useLayoutEffect(() => {
    const prev = last.current;
    const idx = historyIdx();
    last.current = { key, pathname, idx };
    if (prev?.key === key) return;
    // A swipe that led here has already shown this page arriving, if it had a copy of it.
    const swipe = layer;
    resetPage();
    stopRestoring();
    const page = getPage();
    if (!prev || !page) return;
    if (navigationType === 'POP') restoreScroll(page, scrollTops.get(idx) ?? 0);
    else if (pathname !== prev.pathname) page.scrollTop = 0;
    if (!isMobile) return;
    if (swipe) {
      if (!swipe.copy) slideIn(page);
    } else if (
      navigationType === 'POP' &&
      idx < prev.idx &&
      pathname !== prev.pathname &&
      !browserAnimated
    ) {
      slideIn(page);
    }
  }, [key, pathname, navigationType, isMobile]);

  // Copies show the account they were taken in, and none are taken while this is unmounted.
  useEffect(() => copies.clear(), [serviceId]);

  useEffect(() => {
    const onPopState = (e: PopStateEvent & { hasUAVisualTransition?: boolean }) => {
      browserAnimated = e.hasUAVisualTransition === true;
    };
    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('popstate', onPopState);
      window.clearTimeout(timer.current);
      stopRestoring();
      resetPage();
    };
  }, []);

  const settle = (page: HTMLElement, passed: boolean, done: () => void) => {
    const ms = reducedMotion() ? 0 : SETTLE_MS;
    const transition = `transform ${ms}ms var(--ease-standard), opacity ${ms}ms var(--ease-standard)`;
    settling.current = true;
    page.style.transition = transition;
    page.style.transform = passed ? 'translateX(100%)' : 'translateX(0)';
    if (layer) {
      layer.root.style.transition = transition;
      layer.dim.style.transition = transition;
      moveUnder(passed ? 1 : 0);
    }
    timer.current = window.setTimeout(() => {
      settling.current = false;
      done();
    }, ms);
  };

  useSwipeRight({
    enabled: isMobile,
    surfaceFor: (target) => {
      const page = getPage();
      if (settling.current || !page?.contains(target) || !getBackHandler(navigate)) return null;
      window.clearTimeout(timer.current);
      return page;
    },
    onStart: (page) => {
      // A page still sliding in from a back without a swipe is put in place first.
      resetPage();
      page.style.transition = 'none';
      pastThreshold.current = false;
      dragFrom.current = window.location.href;
      // A drag sends no mousedown, so a menu that closes on an outside click would otherwise
      // stay open over the page as it leaves.
      document.body.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
      page.style.boxShadow = EDGE_SHADOW;
      // The page has no fill of its own; without one the layer below would show through it.
      page.style.background = 'var(--bg-main)';
      pinned = pinFixed(page);
      showUnder(page);
    },
    onMove: (page, deltaX) => {
      moveUnder(deltaX / page.clientWidth);
      // A tick as the drag passes the point where letting go goes back (native app only).
      const past = deltaX >= page.clientWidth * CLOSE_THRESHOLD;
      if (past && !pastThreshold.current) triggerHaptic(ImpactStyle.Light);
      pastThreshold.current = past;
    },
    onRelease: (page, passed) => {
      const handler = getBackHandler(navigate);
      const from = dragFrom.current;
      // The route changed under the finger (Android back, a redirect), so this drag is over.
      if (!passed || !handler || window.location.href !== from) {
        settle(page, false, resetPage);
        return;
      }
      settle(page, true, () => {
        // Something else moved the app on while the page slid away.
        if (window.location.href !== from) return;
        void handler();
        timer.current = window.setTimeout(() => {
          // Still here: the handler changed something other than the route, or nothing, so
          // the page comes back. A route change is still on its way and resets it on arrival.
          if (window.location.href !== from) return;
          resetPage();
          slideIn(page);
        }, RESET_FALLBACK_MS);
      });
    },
  });

  return <RememberPageOnLeave idx={historyIdx()} copy={isMobile} />;
};
